import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { isProd } from '@anm/shared/mode';
import { useCallback, FC } from 'react';

import LeftBarLayout from '../../../components/layouts/LeftBarLayout';
import EditButton from '../EditButton';

import LeaveTabAlert from './LeaveTabAlert';
import ReplaceVideoButton from './ReplaceVideoButton';
import ShareVideo from './ShareVideo';
import { getLayoutNavigation } from './helpers';
import useLeaveAlert from './hooks/useLeaveAlert';
import useTogglers from './hooks/useTogglers';
import { LayoutProps } from './types';
import { BROADCASTS_TAB, CustomizationTabIndexes, DOMAINS_TAB, PREVIEW_TAB, STATS_TAB } from '../../../pages/video';

type LayoutButtonProps = {
  activeButton: CustomizationTabIndexes;
  onActiveButtonChange: (index: CustomizationTabIndexes) => void;
  onDownloadStream(streamId: string): void;
  onActionSheetOpen?(): void;
  showNavigationDropdownBreakpoint?: number;
};

const Layout: FC<LayoutProps & LayoutButtonProps> = ({
  activeButton,
  showNavigationDropdownBreakpoint,
  onDownloadStream,
  onActionSheetOpen,
  onActiveButtonChange,
  ...otherProps
}) => {
  const { hostId, projectId, wasChanged, getHandlers } = otherProps;

  const { save, discard, isPending } = getHandlers();

  const [openTab, setTargetTab, showLeaveAlert, setPrevAlertState, setShowLeaveAlert] = useLeaveAlert<
    CustomizationTabIndexes
  >({
    projectId,
    isPending,
    onActiveButtonChange
  });

  const [togglers] = useTogglers<CustomizationTabIndexes>({
    hostId,
    wasChanged,
    activeButton,
    openTab
  });

  const handleCloseAlert = useCallback(() => {
    setTargetTab(null);
    setShowLeaveAlert(false);
  }, []);

  const handleSwitchTab = useCallback(
    (tab: CustomizationTabIndexes) => {
      setTargetTab(tab);

      switch (true) {
        case wasChanged && isMobileOrIPad():
          discard();
          openTab(tab);
          return;

        case wasChanged:
          setPrevAlertState(true);
          setShowLeaveAlert(true);
          return;

        default:
          openTab(tab);
      }
    },
    [wasChanged]
  );

  const handleDiscardChanges = () => {
    discard();
    setShowLeaveAlert(false);
  };

  const isPreviewTab = activeButton === PREVIEW_TAB;
  const isStatsTab = activeButton === STATS_TAB;
  const isBroadcastsTab = activeButton === BROADCASTS_TAB;
  const isDomainsTab = activeButton === DOMAINS_TAB;

  const leftBarLayoutVariant = isPreviewTab
    ? 'no-content'
    : isStatsTab || isBroadcastsTab || isDomainsTab
    ? 'full-size'
    : 'split';

  return (
    <>
      {showLeaveAlert && (
        <LeaveTabAlert {...{ isPending }} onClose={handleCloseAlert} onConfirm={save} onCancel={handleDiscardChanges} />
      )}
      <LeftBarLayout
        {...{
          activeButton,
          onActiveButtonChange,
          onActionSheetOpen,
          showNavigationDropdownBreakpoint
        }}
        navButtonVariant="white"
        variant={leftBarLayoutVariant}
        list={getLayoutNavigation({
          ...otherProps,
          handleSwitchTab,
          onDownloadStream,
          togglers
        })}
        renderNavBottomContent={isProd ? undefined : () => <ReplaceVideoButton />} //todo: handle replace in uploads saga
        renderNavTopContent={() => (!isMobileOrIPad() ? <EditButton projectId={projectId} /> : null)}
        renderNavRightContent={() => (isMobileOrIPad() ? <ShareVideo {...{ hostId, projectId }} /> : null)}
      />
    </>
  );
};

export default Layout;
